/* Custom ingredients showcase — card expand toggle + carousel nav.

   Mobile uses the same infinite-loop pattern as the testimonials
   carousel: clone the first card to the end and the last card to the
   start, set the initial scroll past the tail clone, and silently
   teleport from the clone region back to the equivalent real-card
   position once the scroll settles. Desktop hides the clones via CSS so
   the 2-col grid only shows real cards. */
(function () {
  if (customElements.get('ingredients-carousel')) return;

  const DESKTOP_MQ = '(min-width: 990px)';
  const SCROLL_SETTLE_MS = 80;

  class IngredientsCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;

      // Snapshot the real cards before we splice in clones.
      this.originals = Array.from(this.track.querySelectorAll('[data-card]'));
      this.realCount = this.originals.length;
      // 2 clones each side. With only 1, fast swipes can outrun the
      // wrap-settle timer, hit the rail boundary, and leave scroll-snap
      // stuck between cards. Clamped to realCount in case the user only
      // has 2-3 ingredients.
      this.cloneCount = Math.min(2, this.realCount);

      const loopEnabled = this.realCount > 1;
      if (loopEnabled) this._addClones();

      // Re-query after possible clone insertion so listeners + height
      // measurements include clones.
      this.cards = Array.from(this.track.querySelectorAll('[data-card]'));
      this.toggles = Array.from(this.querySelectorAll('[data-toggle]'));
      this.prevBtn = this.querySelector('[data-prev]');
      this.nextBtn = this.querySelector('[data-next]');
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));

      // Clear any leftover inline track height that an earlier version of
       // this script set — CSS Grid handles row sizing now.
      this.track.style.height = '';

      this._setupToggles();
      this._setupArrows();
      this._setupDots();
      this._setupScrollSync();
      this._setupHeaderHeights();

      if (loopEnabled) {
        requestAnimationFrame(() => {
          this._setInitialPosition();
          this._setupScrollLoopWatcher();
        });
      }
    }

    /* Clone last N real cards at the start (in original order so reading
       right-to-left from real0 the visual sequence is real_n-2, real_n-1),
       and clone first N at the end. data-clone lets CSS hide them on
       desktop and JS map them back to originals. */
    _addClones() {
      const tails = this.originals.slice(-this.cloneCount);
      tails.forEach((c) => {
        const clone = c.cloneNode(true);
        clone.setAttribute('data-clone', 'tail');
        clone.setAttribute('aria-hidden', 'true');
        // insertBefore the current firstChild — last iteration ends up
        // leftmost, giving us [tail_n-2-clone, tail_n-1-clone, real0...]
        this.track.insertBefore(clone, this.track.firstChild);
      });
      const heads = this.originals.slice(0, this.cloneCount);
      heads.forEach((c) => {
        const clone = c.cloneNode(true);
        clone.setAttribute('data-clone', 'head');
        clone.setAttribute('aria-hidden', 'true');
        this.track.appendChild(clone);
      });
    }

    _firstRealCard() {
      return this.track.children[this.cloneCount];
    }
    _lastRealCard() {
      return this.track.children[this.cloneCount + this.realCount - 1];
    }
    _firstHeadClone() {
      return this.track.children[this.cloneCount + this.realCount];
    }
    _firstTailCloneWidth() {
      const last = this.track.children[this.cloneCount - 1];
      if (!last) return 0;
      return last.offsetLeft - this.track.offsetLeft + last.offsetWidth;
    }

    _setInitialPosition() {
      const firstReal = this._firstRealCard();
      if (!firstReal) return;
      this._jumpTo(firstReal.offsetLeft - this.track.offsetLeft);
    }

    /* Set scrollLeft with no smooth animation — for the silent teleport
       when crossing out of the clone region. */
    _jumpTo(left) {
      const prev = this.track.style.scrollBehavior;
      this.track.style.scrollBehavior = 'auto';
      this.track.scrollLeft = left;
      // Force a reflow to commit the position.
      // eslint-disable-next-line no-unused-expressions
      this.track.scrollLeft;
      this.track.style.scrollBehavior = prev || '';
    }

    _setupScrollLoopWatcher() {
      let scrollTimer;
      const schedule = (delay) => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => this._maybeWrap(), delay);
      };
      this.track.addEventListener('scroll', () => schedule(SCROLL_SETTLE_MS), { passive: true });
      this.track.addEventListener('touchend', () => schedule(180), { passive: true });
    }

    /* Snap-to-card wrap: figure out which clone card the user landed on
       (head or tail region) and teleport to its real twin's exact
       offsetLeft. Going by offsetLeft instead of offset arithmetic
       ensures we always land on a snap point — otherwise on iOS Safari
       scroll-snap fights the teleport and the card visibly sits
       between two snap positions. */
    _maybeWrap() {
      if (window.matchMedia(DESKTOP_MQ).matches) return;

      const firstReal = this._firstRealCard();
      const firstHeadClone = this._firstHeadClone();
      if (!firstReal || !firstHeadClone) return;

      const realStart = firstReal.offsetLeft - this.track.offsetLeft;
      const headStart = firstHeadClone.offsetLeft - this.track.offsetLeft;
      const scroll = this.track.scrollLeft;
      const stride = firstReal.offsetWidth + this._getGap();
      const slack = 4;

      if (scroll >= headStart - slack) {
        // In head clones — head[i] is a clone of real[i].
        const cloneIdx = Math.max(
          0,
          Math.min(this.cloneCount - 1, Math.round((scroll - headStart) / stride))
        );
        const target = this.track.children[this.cloneCount + cloneIdx];
        if (target) this._jumpTo(target.offsetLeft - this.track.offsetLeft);
      } else if (scroll < realStart - slack) {
        // In tail clones — tail[i] is a clone of real[realCount - cloneCount + i].
        const tailIdx = Math.max(
          0,
          Math.min(this.cloneCount - 1, Math.round(scroll / stride))
        );
        const realIdx = this.realCount - this.cloneCount + tailIdx;
        const target = this.track.children[this.cloneCount + realIdx];
        if (target) this._jumpTo(target.offsetLeft - this.track.offsetLeft);
      }
    }

    _getGap() {
      const styles = getComputedStyle(this.track);
      return parseInt(styles.columnGap || styles.gap, 10) || 0;
    }

    /* Each card has a +/- button. If the user taps a clone's toggle we
       mirror the new state to the original so after teleport they don't
       see a different state. With multiple clones now, the head clone
       at DOM index (cloneCount + realCount + N) maps to real[N], and
       tail[N] (DOM index N) maps to real[realCount - cloneCount + N]. */
    _setupToggles() {
      this.toggles.forEach((btn) => {
        btn.addEventListener('click', () => {
          const card = btn.closest('[data-card]');
          if (!card) return;

          let sourceCard = card;
          if (card.dataset.clone === 'head' || card.dataset.clone === 'tail') {
            const realIdx = this._realIndexForClone(card);
            if (realIdx >= 0) sourceCard = this.originals[realIdx];
          }
          const sourceBtn = sourceCard?.querySelector('[data-toggle]');

          const wasExpanded = sourceCard.dataset.expanded === 'true';
          const newState = wasExpanded ? 'false' : 'true';

          sourceCard.dataset.expanded = newState;
          sourceBtn?.setAttribute('aria-expanded', newState);
          if (card !== sourceCard) {
            card.dataset.expanded = newState;
            btn.setAttribute('aria-expanded', newState);
          }
          // CSS Grid recalculates row height automatically as the card's
          // max-height transition runs — no JS height management needed.
        });
      });
    }

    _realIndexForClone(card) {
      const domIdx = Array.from(this.track.children).indexOf(card);
      if (domIdx < 0) return -1;
      if (card.dataset.clone === 'tail') {
        // tail[N] (DOM index N) → real[realCount - cloneCount + N]
        return this.realCount - this.cloneCount + domIdx;
      }
      if (card.dataset.clone === 'head') {
        // head[N] (DOM index cloneCount + realCount + N) → real[N]
        return domIdx - this.cloneCount - this.realCount;
      }
      return -1;
    }

    _setupArrows() {
      this.prevBtn?.addEventListener('click', () => this._scrollByCards(-1));
      this.nextBtn?.addEventListener('click', () => this._scrollByCards(1));
    }

    _scrollByCards(direction) {
      const card = this.cards[0];
      if (!card) return;
      const styles = getComputedStyle(this.track);
      const gap = parseInt(styles.columnGap || styles.gap, 10) || 0;
      const cardWidth = card.getBoundingClientRect().width;
      const distance = (cardWidth + gap) * direction;
      this.track.scrollBy({ left: distance, behavior: 'smooth' });
    }

    _setupDots() {
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          // Real card at index N is at DOM position cloneCount + N.
          const card = this.track.children[this.cloneCount + index];
          if (!card) return;
          this.track.scrollTo({
            left: card.offsetLeft - this.track.offsetLeft,
            behavior: 'smooth',
          });
        });
      });
    }

    /* Watch scroll → highlight the dot of the active real card. The
       track height is NOT managed here anymore — CSS Grid handles it.
       Removing the JS height management was what finally unblocked the
       scroll-snap glitch when one card was open and the user swiped
       to a closed one. */
    _setupScrollSync() {
      let ticking = false;
      const updateDots = () => {
        ticking = false;
        const activeIndex = this._getActiveRealIndex();
        this.dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === activeIndex);
        });
      };
      const onScroll = () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(updateDots);
        }
      };
      this.track.addEventListener('scroll', onScroll, { passive: true });
      requestAnimationFrame(updateDots);
    }

    _setupHeaderHeights() {
      requestAnimationFrame(() => this._normalizeHeaderHeights());
      window.addEventListener(
        'resize',
        () => this._normalizeHeaderHeights(),
        { passive: true }
      );
    }

    /* Make every card's HEADER the same height so the image (which
       stretches via align-self: stretch) ends up uniform across all
       cards. Without this, headers size to their own text content
       (e.g. a 2-line title is taller than a 1-line one) and the
       images visibly differ — most noticeable on the desktop 2-col
       grid where you can see all 4 side by side, but also on the
       mobile carousel as you scroll between cards. Clones contribute
       0 height on desktop because they're display: none, so they
       don't skew the max. */
    _normalizeHeaderHeights() {
      if (!this.cards.length) return;

      // Always clear first so a previous min-height doesn't pollute
      // the re-measurement (e.g. on resize / breakpoint flip when
      // text reflow changes natural heights).
      this.cards.forEach((c) => {
        const header = c.querySelector('.custom-ingredients__card-header');
        if (header) header.style.minHeight = '';
      });

      let maxH = 0;
      this.cards.forEach((c) => {
        const header = c.querySelector('.custom-ingredients__card-header');
        if (!header) return;
        const h = header.getBoundingClientRect().height;
        if (h > maxH) maxH = h;
      });
      if (!maxH) return;

      this.cards.forEach((c) => {
        const header = c.querySelector('.custom-ingredients__card-header');
        if (header) header.style.minHeight = maxH + 'px';
      });
    }

    _findClosestCard() {
      if (!this.cards.length) return null;
      const center = this.track.scrollLeft + this.track.clientWidth / 2;
      let result = this.cards[0];
      let minDist = Infinity;
      this.cards.forEach((card) => {
        const cardMid = card.offsetLeft - this.track.offsetLeft + card.offsetWidth / 2;
        const dist = Math.abs(cardMid - center);
        if (dist < minDist) {
          minDist = dist;
          result = card;
        }
      });
      return result;
    }

    /* Map a closest card (possibly a clone) back to the index of the
       real card it represents. With cloneCount > 1, head clone N maps
       to real N, tail clone N maps to real[realCount - cloneCount + N]. */
    _getActiveRealIndex() {
      const closest = this._findClosestCard();
      if (!closest) return -1;
      if (closest.dataset.clone === 'head' || closest.dataset.clone === 'tail') {
        const realIdx = this._realIndexForClone(closest);
        if (realIdx >= 0) return realIdx;
      }
      return this.originals.indexOf(closest);
    }

  }

  customElements.define('ingredients-carousel', IngredientsCarousel);
})();
